<template>
  <main class="container mx-auto mt-16 py-5 md:pt-10 md:pb-15">
    <div class="flex justify-between mb-8 md:mb-12">
      <h1 class="section-heading">Podcasts</h1>
    </div>

    <div
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-12"
    >
      <template v-if="isLoading">
        <div v-for="i in 4" :key="i" class="flex flex-col gap-4">
          <UtilsSkeletonLoading class="h-44" />
          <UtilsSkeletonLoading class="h-10" />
        </div>
      </template>
      <template v-else>
        <CommonPodcastCard
          v-for="item in contentsData"
          :key="item.id"
          :data="item"
        />
        <template v-if="isLoadingMore">
          <div v-for="i in 4" :key="i" class="flex flex-col gap-4">
            <UtilsSkeletonLoading class="h-44" />
            <UtilsSkeletonLoading class="h-10" />
          </div>
        </template>
      </template>
    </div>

    <div
      v-if="contentMeta?.pagination?.page < contentMeta?.pagination?.pageCount"
      class="mx-auto text-center mt-12"
    >
      <CommonLoadMoreButton @click="loadMore" />
    </div>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

const route = useRoute()

const isLoading = ref(true)
const isLoadingMore = ref(false)
const page = ref(1)

const SEOMeta = ref({})
const contentsData = ref([])
const contentMeta = ref({})

const [response, seo] = await Promise.all([
  useSuitmediaApiData(`/api/contents`, {
    query: {
      'pagination[page]': page,
      'pagination[pageSize]': 16,
      populate: 'image,layout_component,layout_component.type',
      'filters[$and][0][layout][$eq]': 'podcast'
    }
  }),
  useSuitmediaApiData(`/api/seo-metas`, {
    query: {
      'filters[url]': route.path,
      populate: 'seo.image'
    }
  })
])

isLoading.value = response.pending.value

if (response.error.value) {
  throw createError({
    statusCode: 404,
    message: `Podcasts does not exist`
  })
}

SEOMeta.value = seo.data.value.data[0]

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: SEOMeta?.value?.attributes?.url || 'suitmedia.com',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

useSeoMeta({
  title: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: SEOMeta?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: SEOMeta?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    SEOMeta?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    SEOMeta?.value?.attributes?.seo?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

contentsData.value = response.data.value.data
contentMeta.value = response.data.value.meta

const getContents = async () => {
  const { data, error, pending } = await useSuitmediaApiData(`/api/contents`, {
    query: {
      'pagination[page]': page,
      'pagination[pageSize]': 16,
      populate: 'image,layout_component,layout_component.type',
      'filters[$and][0][layout][$eq]': 'podcast'
    }
  })
  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Case Studies does not exist`
    })
  }

  return data.value
}

const loadMore = async () => {
  isLoadingMore.value = true
  page.value++
  const addClients = await getContents()
  contentsData.value = [...contentsData.value, ...addClients.data]
  contentMeta.value = addClients.meta
  isLoadingMore.value = false
  global.changeLoadedMore()
}
</script>

<style lang="scss" scoped>
.filter {
  :deep(.vs--open) {
    .vs__dropdown-toggle {
      padding: 10px;

      background: linear-gradient(#ff6700, #ff6700),
        linear-gradient(#ff6700, #ff6700), linear-gradient(#ff6700, #ff6700);
      background-size:
        1px 20%,
        100% 1px,
        1px 20%;
      background-position:
        bottom left,
        bottom center,
        bottom right;
      background-repeat: no-repeat;

      border: none;
    }
  }
  :deep(.vs__dropdown-toggle) {
    padding: 10px;

    background: linear-gradient(#a5a5a5, #a5a5a5),
      linear-gradient(#a5a5a5, #a5a5a5), linear-gradient(#a5a5a5, #a5a5a5);
    background-size:
      1px 20%,
      100% 1px,
      1px 20%;
    background-position:
      bottom left,
      bottom center,
      bottom right;
    background-repeat: no-repeat;

    border: none;
    border-radius: 0;
    // color: #999;
  }
}
</style>
